import { getLogger } from "@expert/logging";
import { ActionIcon, Flex, Text, UnstyledButton } from "@mantine/core";
import { useEffect, useState } from "react";
import { useAnalytics } from "@/analytics";
import { CloseX } from "@/common-ui";
import { BaseNotification } from "../../BaseNotification/BaseNotification";
import { defaultReactions, optionalReactions, type SaleMadeNotificationType } from "../../models";
import { sendReaction } from "../api/sendReaction";
import styles from "./SaleMadeNotification.module.css";

const logger = getLogger({
    module: "SaleMadeNotification",
    supportTeam: "sales",
});

export function SaleMadeNotification({
    onClose,
    seller: { employeeName, employeeId, rank, teamSize, mascot },
}: SaleMadeNotificationType["props"] & {
    onClose: () => void;
}) {
    const { dispatcher } = useAnalytics();
    const [chosenReaction, setChosenReaction] = useState<string | null>(null);
    const [isClosing, setIsClosing] = useState(false);
    setTimeout(() => setIsClosing(true), 10000);

    useEffect(() => {
        void dispatcher.dispatchBusinessEvent("SaleMadeNotificationReceived", { seller: employeeId });
        logger.info({ seller: employeeId }, "SaleMadeNotificationReceived");
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const reactionsToRender = [...defaultReactions, ...optionalReactions.filter((r) => r.id === mascot)];

    return (
        <BaseNotification onClose={onClose} isClosing={isClosing} side="left">
            <Flex direction="column">
                <Text c="dark.1" fw={700} size="sm">
                    {employeeName} made a sale!
                </Text>
                <Text c="dark.1" size="sm">
                    Now ranked {rank} of {teamSize}
                </Text>
            </Flex>
            <Flex className={styles.buttonContainer} ml="xl">
                {chosenReaction ? (
                    <Text className={styles.fadeIn} size="sm">
                        {chosenReaction} sent
                    </Text>
                ) : (
                    <Text size="sm">Send reaction</Text>
                )}
                {!chosenReaction &&
                    reactionsToRender.map((r) => (
                        <Flex key={r.id} pl={6}>
                            <UnstyledButton
                                onClick={() => {
                                    void dispatcher.dispatchBusinessEvent("ReactionSent", {
                                        reactionType: r.id,
                                        sentTo: employeeId,
                                        mascot,
                                    });
                                    logger.info({ seller: employeeId, reactionType: r.id }, "ReactionSent");
                                    setChosenReaction(r.id);
                                    void sendReaction(employeeId, r.id);
                                    setTimeout(() => setIsClosing(true), 3000);
                                }}
                                key={r.id}
                                bg="var(--mantine-color-primary-2)"
                                className={styles.round}
                            >
                                {r.value}
                            </UnstyledButton>
                        </Flex>
                    ))}
                <ActionIcon
                    onClick={() => {
                        void dispatcher.dispatchBusinessEvent("SaleMadeNotificationDismissed");
                        logger.info({ seller: employeeId }, "SaleMadeNotificationDismissed");
                        setIsClosing(true);
                    }}
                    ml="xxs"
                    mr={0}
                    bg="transparent"
                >
                    <CloseX fill="var(--mantine-color-primary-1)" />
                </ActionIcon>
            </Flex>
        </BaseNotification>
    );
}
